import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const DatePopover = ({ isOpen, onClose, onApply, position }) => {
  const { t } = useTranslation();
  const [fromDate, setFromDate] = useState('2025-09-28');
  const [toDate, setToDate] = useState('2025-09-28');
  const [selectedPreset, setSelectedPreset] = useState('yesterday');
  const popoverRef = useRef(null);

  const predefinedOptions = [
    { id: 'yesterday', label: t('transactions.datePopover.yesterday'), checked: true },
    { id: 'today', label: t('transactions.datePopover.today'), checked: false },
    // { id: 'tomorrow', label: 'Tomorrow', checked: false },
    { id: 'lastWeek', label: t('transactions.datePopover.lastWeek'), checked: false },
    { id: 'thisWeek', label: t('transactions.datePopover.thisWeek'), checked: false },
    { id: 'lastMonth', label: t('transactions.datePopover.lastMonth'), checked: false },
    { id: 'thisMonth', label: t('transactions.datePopover.thisMonth'), checked: false },
    { id: 'nextMonth', label: t('transactions.datePopover.nextMonth'), checked: false },
    { id: 'lastYear', label: t('transactions.datePopover.lastYear'), checked: false },
    { id: 'thisYear', label: t('transactions.datePopover.thisYear'), checked: false },
  ];

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handlePresetChange = (presetId) => {
    setSelectedPreset(presetId);
    // Here you would typically calculate the actual dates based on the preset

  };

  const handleApply = () => {
    onApply({
      fromDate,
      toDate,
      preset: selectedPreset
    });
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={popoverRef}
      className="fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-3 sm:p-4 w-72 sm:w-80 max-w-[calc(100vw-2rem)]"
      style={{
        top: position?.top || 0,
        left: position?.left || 0,
        transform: 'translateX(0)',
      }}
    >
      {/* Date Input Fields */}
      <div className="space-y-3 sm:space-y-4 mb-3 sm:mb-4">
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
            {t('transactions.datePopover.from')}
          </label>
          <div className="relative">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full px-2 sm:px-3 py-2 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-sm"
              style={{ backgroundColor: '#F6F7F8' }}
            />
            <div className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2">
              <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>
        
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
            {t('transactions.datePopover.to')}
          </label>
          <div className="relative">
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full px-2 sm:px-3 py-2 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-sm"
              style={{ backgroundColor: '#F6F7F8' }}
            />
            <div className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2">
              <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Predefined Options */}
      <div className="mb-3 sm:mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2">
          {predefinedOptions.map((option) => (
            <label
              key={option.id}
              className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1.5 sm:p-2 rounded"
            >
              <input
                type="radio"
                name="datePreset"
                checked={selectedPreset === option.id}
                onChange={() => handlePresetChange(option.id)}
                className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
              />
              <span className="text-xs sm:text-xs text-gray-700">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-end">
        <button
          onClick={handleCancel}
          className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors w-full sm:w-auto"
        >
          {t('transactions.datePopover.cancel')}
        </button>
        <button
          onClick={handleApply}
          className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors w-full sm:w-auto"
        >
          {t('transactions.datePopover.apply')}
        </button>
      </div>
    </div>
  );
};

export default DatePopover;
